import { Component, Input, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loadedMenusState,
  loadedTenantState,
} from "src/app/store/reducers";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FilterName,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { ProductPriceSchedulingDTO, ProductPriceSchedulingStatus } from "src/app/models/Products/priceSchedulingDTO";
import { ProductPriceSchedulingService } from "src/app/shared/services/products/products/productPriceScheduling.service";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { ActivatedRoute } from "@angular/router";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { Title } from "@angular/platform-browser";
import {
  CHIP_STYLE,
  getChipStyle,
} from "../../../shared/utils/customTableUtils";
import {
  DefaultConfirmationDialogData,
  Dialog,
} from "src/app/models/dialog/dialog.model";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { AdvancedFilter } from '../../../models/custom-table/custom-table.model';

const USAGE_CODE = "PRODUCT.PRICESCHEDULING";

@Component({
  selector: "app-product-price-scheduling-list",
  templateUrl: "./product-price-scheduling-list.component.html",
  styleUrls: ["./product-price-scheduling-list.component.scss"],
})
export class ProductPriceSchedulingListComponent implements OnInit {
@Input() siteNumber: string;
@Input() productCode: string;

  initialSort: SortEvent = {
    active: "createdOn",
    direction: "DESC",
  };

  isLoading$ = new BehaviorSubject<boolean>(false);
  totalProductPriceScheduling$ = new BehaviorSubject<number>(0);
  columnInfo$: Observable<ColumnSorterInfo[]>;

  public creationFormPath: string = "price-scheduling/new";
  public formPath = "price-scheduling";

  productPriceSchedulingList: ProductPriceSchedulingDTO[];

  productPriceSchedulingList$: Observable<ProductPriceSchedulingDTO[]>;
  excelProductPriceSchedulingList$: Observable<ProductPriceSchedulingDTO[]>;
  currencySymbol: string = "";

  headerIcon: string;
  tableTitle: string;

  //menu
  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  public selectedRow: object = {};

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "ProductPriceScheduling.token",
      field: "token",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
      searchable: false
    },
    {
      header: "SiteManagement.SiteNumber",
      field: "siteNumber",
      fieldType: FieldType.STRING,
    },
    {
      header: "ProductPriceScheduling.productCode",
      field: "productCode",
      fieldType: FieldType.STRING,
      searchable: false,
    },
    {
      header: "ProductPriceScheduling.productDescription",
      field: "productDescription",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "ProductPriceScheduling.status",
      field: "status",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "ProductPriceScheduling.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "ProductPriceScheduling.dateTimeStart",
      field: "dateTimeStart",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "ProductPriceScheduling.statusUpdatedOn",
      field: "statusUpdatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "ProductPriceScheduling.oldPrice",
      field: "oldPrice",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.CURRENCY,
      hidden: true,
    },
    {
      header: "ProductPriceScheduling.newPrice",
      field: "newPrice",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.CURRENCY,
    },
    {
      header: "ProductPriceScheduling.comment",
      field: "comment",
      fieldType: FieldType.STRING,
      searchable: true,
    },
  ];

  // loaded locale
  public locale: string = localStorage.getItem("locale");

  //Enabling Create Button only if on PriceScheduling Menu
  enabling: boolean;


  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private titleService: Title,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private productPriceSchedulingService: ProductPriceSchedulingService
  ) {}

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.enabling = this.siteNumber ? false : this.productCode ? false : true;

    this.loadScreenTitle();
    this.getTableConfig();
    this.getProductPriceSchedulingList();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getProductPriceSchedulingList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    let advancedFilter: FilterEvent = {
      advancedFilter: [],
    };

    /**Filtro só será usado para Produtos de Loja e quando receber um Input SiteNumber */
    if(this.siteNumber){
      advancedFilter.advancedFilter.push(
      {
        column: "siteNumber",
        fieldType: FieldType.STRING,
        operator: FilterName.EQUALS_TO,
        value: this.siteNumber
      });
    }

    /** Filtro só será usado para Produtos e quando receber um Input Product Code */
    if(this.productCode) {
    advancedFilter.advancedFilter.push(
      {
        column: "productCode",
        fieldType: FieldType.STRING,
        operator: FilterName.EQUALS_TO,
        value: this.productCode
      });
    }

    this.productPriceSchedulingList$ = this.productPriceSchedulingService
      .getPriceSchedulerList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent ?? this.initialSort,
        advancedFilter ?? filterEvent
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalProductPriceScheduling$.next(size);
          const treatedItems = items.map((item: ProductPriceSchedulingDTO) =>
            this.setCustomFields(item)
          );
          return treatedItems;
        }),

        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Products.productSuppliersLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  openDeleteDialog(priceSchedulingToken: any) {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.onDeleteRow(priceSchedulingToken);
      }
    });
  }

  onDeleteRow(priceSchedulingToken: any) {
    this.isLoading$.next(true);
    this.productPriceSchedulingService
      .cancelProductPriceScheduling(priceSchedulingToken)
      .pipe(
        tap(() => {
          this.getProductPriceSchedulingList();
          this.snackbarService.show({
            message: this.translate.instant(
              "ProductPriceScheduling.deleteSuccess"
            ),
            type: SnackbarType.SUCCESS,
          });
        }),
        catchError((error) => {
          this.snackbarService.show({
            message:
              this.translate.instant(error) ??
              this.translate.instant("PriceScheduling.deleteError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  getProductPriceSchedulingExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    this.excelProductPriceSchedulingList$ = this.productPriceSchedulingService
      .getPriceSchedulerList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
        },
        searchEvent,
        sortEvent ?? this.initialSort,
        filterEvent
      )
      .pipe(
        map(({ items }) => items),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Entity.entitiesLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  setCustomFields(productPriceScheduling: any) {
    const defaultProductPriceScheduling = {
      ...productPriceScheduling,
    };
    const { status } = productPriceScheduling;
    if (status === undefined) {
      return defaultProductPriceScheduling;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
    if (status !== undefined && status !== null) {
      switch (status) {
        case ProductPriceSchedulingStatus.DEFINED:
        case ProductPriceSchedulingStatus.DEFINED_CLIENT:
        case ProductPriceSchedulingStatus.PENDING_FCC:
          style = CHIP_STYLE.STARTED;
          break;
        case ProductPriceSchedulingStatus.CHANGED:
          style = CHIP_STYLE.SUCCESS;
          break;
        case ProductPriceSchedulingStatus.INVALID:
          style = CHIP_STYLE.DANGER;
          break;
        case ProductPriceSchedulingStatus.EXPIRED:
        case ProductPriceSchedulingStatus.CANCELLED:
          style = CHIP_STYLE.INACTIVE;
          break;
      }

      let styleSettings = getChipStyle(style);
      let statusChip = {
        ...productPriceScheduling,
        disableDelete: status !== ProductPriceSchedulingStatus.DEFINED,
        status: {
          value: this.translate.instant(
            "ProductPriceScheduling.statusConstants." + status
          ),
          icon: styleSettings.icon,
          style: styleSettings.style,
        },
      };
      return statusChip;
    }
  }

  getTableConfig() {
    this.columnInfo$ = this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.LoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getProductPriceSchedulingList(
      pageEvent,
      sortEvent ?? this.initialSort,
      searchEvent,
      filterEvent
    );
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getProductPriceSchedulingExcelList(
      pageEvent,
      sortEvent ?? this.initialSort,
      searchEvent,
      filterEvent
    );
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
  }
}
